/**
 * InboxView — Shows threads that require attention (interrupted with pending tasks).
 *
 * Uses @tanstack/react-query for caching — navigating away and back shows
 * cached data instantly, with a background refetch if stale (>30s).
 */

import { type FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClockIcon,
  InboxIcon,
  LoaderIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { listThreadsWithDates, getThreadState, type ThreadWithDates } from '@/client/lib/chatApi';
import { extractTasks, type Task } from '@/client/lib/tasksContext';
import { cn } from '@/client/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InboxThread extends ThreadWithDates {
  pendingTasks: Task[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Fetch interrupted threads and enrich with pending tasks. */
async function fetchInboxThreads(): Promise<InboxThread[]> {
  const { threads: interrupted } = await listThreadsWithDates({
    limit: 50,
    offset: 0,
    status: 'interrupted',
    sortBy: 'updated_at',
    sortOrder: 'desc',
  });

  const enriched = await Promise.all(
    interrupted.map(async (thread) => {
      try {
        const state = await getThreadState(thread.thread_id);
        const tasks = extractTasks(state.values as Record<string, unknown>);
        const pendingTasks = tasks.filter((t) => t.status === 'pending');
        return { ...thread, pendingTasks };
      } catch {
        return { ...thread, pendingTasks: [] as Task[] };
      }
    }),
  );

  return enriched.filter((t) => t.pendingTasks.length > 0);
}

// ---------------------------------------------------------------------------
// InboxView
// ---------------------------------------------------------------------------

interface InboxViewProps {
  onSelectThread: (threadId: string) => void;
}

export const InboxView: FC<InboxViewProps> = ({ onSelectThread }) => {
  const {
    data: threads = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['inbox-threads'],
    queryFn: fetchInboxThreads,
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load inbox'}</p>
        <button
          onClick={() => void refetch()}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
        >
          <RefreshCwIcon className="size-3" />
          Retry
        </button>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <InboxIcon className="size-8 text-muted-foreground/40" />
        <p className="text-sm font-medium text-foreground">All caught up</p>
        <p className="text-xs text-muted-foreground">
          No threads need your attention right now.
        </p>
      </div>
    );
  }

  const totalPendingTasks = threads.reduce((sum, t) => sum + t.pendingTasks.length, 0);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2.5 text-[11px] tabular-nums text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-medium text-primary">
              <InboxIcon className="size-3" />
              {threads.length} {threads.length === 1 ? 'conversation' : 'conversations'}
            </span>
            <span className="text-border">&middot;</span>
            <span className="inline-flex items-center gap-1 font-medium text-warning">
              <ClockIcon className="size-3" />
              {totalPendingTasks} pending tasks
            </span>
          </div>
        </div>
        <button
          onClick={() => void refetch()}
          className={cn(
            'flex cursor-pointer items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            isFetching && 'animate-spin',
          )}
          aria-label="Refresh"
        >
          <RefreshCwIcon className="size-3.5" />
        </button>
      </div>

      {/* Thread cards */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="flex flex-col gap-2">
          {threads.map((thread) => (
            <button
              key={thread.thread_id}
              onClick={() => onSelectThread(thread.thread_id)}
              className={cn(
                'group flex w-full cursor-pointer flex-col gap-2.5 rounded-lg border bg-background p-3.5 text-left transition-all',
                'hover:border-primary/30 hover:shadow-sm',
              )}
            >
              {/* Top row: logo + title + time */}
              <div className="flex items-start gap-2.5">
                <img src="/gaia_logo.svg" alt="" className="size-6 shrink-0 rounded" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground group-hover:text-primary">
                      {thread.title}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {timeAgo(thread.updated_at)}
                    </span>
                  </div>
                  {thread.user_name && (
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      Triggered by {thread.user_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Pending tasks */}
              <div className="flex flex-col gap-1.5 pl-[34px]">
                {thread.pendingTasks.map((task) => (
                  <div key={task.id}>
                    <div className="flex items-center gap-1.5">
                      <ClockIcon className="size-3 shrink-0 text-warning" />
                      <span className="text-[12px] font-medium text-foreground">{task.title}</span>
                    </div>
                    {task.impact && (
                      <p className="ml-[18px] mt-0.5 text-[11px] leading-snug text-muted-foreground">
                        {task.impact}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
